import { useState, type ReactNode } from 'react'
import { ActionButton } from '../../components/Can'
import { useParams } from 'react-router-dom'
import { Badge, Banner } from '@/ui'
import {
  BALANCE_KIND_LABEL,
  balanceVerdict,
  bpText,
  lineageByOutput,
  problemKindText,
  unitTypeLabel,
  useBand,
  useRun,
  type RunBalance,
  type RunDetail,
  type RunOutput,
  type RunProblem,
} from '../../api/process'
import { ExceptionActions } from '../../components/ExceptionActions'
import { ConsolePage, DetailColumns, QueryState } from '../../components/Layout'
import { PhotoViewer } from '../../components/PhotoViewer'
import { Timeline } from '../../components/Timeline'
import { Code, Facts, Kg, RefLink, Section, Severity, StatTile, Status, Stream, UserName, When } from '../../components/bits'
import { BandBadge, YieldBar } from '../../components/process/ProcessBits'
import { bandText } from '../../model/process/format'
import { RunInvestigation } from '../../components/process/RunInvestigation'
import { IssueToRunDialog } from '../../components/process/IssueToRunDialog'
import { canPlanIssue } from '../../model/process/issuePlan'
import { exceptionLabel } from '../../labels'
import { diaryLines, diaryTitle, diaryTone } from '../../model/process/diary'

/** One machine run: balance against the range, bags in, sacks out, lineage, problems, diary. */
export function RunDetailPage() {
  const { id } = useParams<{ id: string }>()
  const q = useRun(id)
  const r = q.data
  return (
    <ConsolePage
      back={{ to: '/c/runs', label: 'Machine runs' }}
      eyebrow="Process · Machine run"
      title={r ? <Code className="text-[inherit]">{r.code}</Code> : 'Machine run'}
      description={r ? `${r.step.label} · ${r.equipmentCode ?? 'no machine'}${r.equipmentName ? ` (${r.equipmentName})` : ''} · ${r.locationName ?? r.locationCode ?? ''}` : undefined}
      actions={
        r ? (
          <span className="flex flex-wrap items-center gap-2">
            <Status status={r.status} />
            <Stream stream={r.stream} />
            {canPlanIssue(r) && <IssueAction run={r} />}
          </span>
        ) : undefined
      }
    >
      <QueryState query={q} label="this run">
        {r && <RunBody r={r} />}
      </QueryState>
    </ConsolePage>
  )
}

function IssueAction({ run }: { run: RunDetail }) {
  const [open, setOpen] = useState(false)
  return (
    <>
      <ActionButton perm="process-runs:plan-issue" hide size="sm" variant="secondary" onClick={() => setOpen(true)}>
        Issue lots to this run
      </ActionButton>
      <IssueToRunDialog run={run} open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function RunBody({ r }: { r: RunDetail }) {
  const b = r.balance
  const v = balanceVerdict(b)
  const openProblems = r.problems.filter((x) => x.status === 'OPEN')
  const balanceProblem = r.problems.find((x) => x.type === 'BALANCE_OUT_OF_BAND' && x.status === 'OPEN') ?? null
  const onHold = r.outputs.filter((o) => o.hold !== 'NONE').length
  return (
    <>
      {r.status === 'EXCEPTION' && (
        <Banner tone="warn" title="This run does not balance">
          Its batches stay on hold until a supervisor who did not run the machine investigates and decides.
          {balanceProblem && (
            <>
              {' '}
              <button
                type="button"
                className="font-semibold underline"
                onClick={() => document.getElementById('problems')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
              >
                Investigate
              </button>
            </>
          )}
        </Banner>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Kilos in" value={<Kg grams={b.inputG} className="text-[inherit]" />} hint={`${r.inputs.scannedIn.length} bags scanned in`} />
        <StatTile label="Kilos out" value={<Kg grams={b.outputG} className="text-[inherit]" />} hint={`${r.outputs.reduce((n, o) => n + o.sacks.length, 0)} sacks`} />
        <StatTile
          label="Left over"
          value={bpText(b.residualBp)}
          tone={v.leftOver === 'WITHIN' ? 'ok' : v.leftOver === 'NO_LIMIT' || v.leftOver === 'NOTHING_IN' ? 'neutral' : 'danger'}
          hint={b.lossLimitBp !== null ? `limit ${bpText(b.lossLimitBp)}` : 'No loss limit'}
        />
        <StatTile
          label="Yield"
          value={bpText(b.yieldBp)}
          tone={v.yield === 'IN_BAND' ? 'ok' : v.yield === 'NO_BAND' ? 'warn' : v.yield === 'NOTHING_IN' ? 'neutral' : 'danger'}
          hint={b.band ? `range ${bandText(b.band)}` : 'No range set'}
        />
      </div>

      <DetailColumns
        main={
          <>
            <BalanceSection r={r} />
            <Section id="problems" title="Problems" eyebrow={openProblems.length ? `${openProblems.length} open` : undefined} bodyClassName="p-0">
              {r.problems.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No problems on this run.</p>
              ) : (
                <ul className="divide-y divide-rule/70">
                  {[...r.problems]
                    .sort((a, c) => (a.status === 'OPEN' ? 0 : 1) - (c.status === 'OPEN' ? 0 : 1))
                    .map((p) => (
                      <ProblemItem key={p.id} run={r} p={p} />
                    ))}
                </ul>
              )}
            </Section>
            <InputsSection r={r} />
            <Section title="Outputs" eyebrow={onHold ? `${onHold} on hold` : undefined} bodyClassName="p-0">
              {r.outputs.length === 0 ? (
                <p className="px-4 py-3 text-[14px] text-muted">No outputs recorded yet.</p>
              ) : (
                r.outputs.map((o) => <OutputBlock key={o.id} r={r} o={o} />)
              )}
            </Section>
            <Section title="Family tree" eyebrow="Where each output came from">
              {r.links.length === 0 ? (
                <p className="text-[14px] text-muted">Linked when the run closes.</p>
              ) : (
                <ul className="flex flex-col gap-2.5">
                  {lineageByOutput(r.links).map((g) => (
                    <li key={g.childUnitId} className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[14px]">
                      <span className="flex flex-wrap gap-x-2">
                        {g.parents.map((p) => (
                          <span key={p.unitId} className="inline-flex items-baseline gap-1">
                            <RefLink to={`/c/batches/${p.unitId}`}>{p.code}</RefLink>
                            {p.qtyG !== null && p.qtyG > 0 && <Kg grams={p.qtyG} className="text-[12px] text-muted" />}
                          </span>
                        ))}
                      </span>
                      <span aria-hidden="true" className="text-muted">→</span>
                      <span className="sr-only">made</span>
                      <RefLink to={`/c/batches/${g.childUnitId}`}>{g.childCode}</RefLink>
                    </li>
                  ))}
                </ul>
              )}
            </Section>
            <Section title="Diary">
              <Timeline
                items={diaryLines(r.events).map((l) => ({
                  id: l.id,
                  at: l.at,
                  until: l.until,
                  span: l.count > 1 && l.until !== l.at,
                  title: diaryTitle(l),
                  detail: l.actorId ? <UserName id={l.actorId} /> : undefined,
                  tone: diaryTone(l),
                }))}
                empty="Nothing recorded yet."
              />
            </Section>
          </>
        }
        side={
          <>
            <Section title="Run">
              <Facts
                columns={2}
                items={[
                  { label: 'Step', value: r.step.label },
                  { label: 'Crop', value: r.cropName },
                  { label: 'Machine', value: r.equipmentCode ? <RefLink to={r.siteId ? `/c/machines?siteId=${r.siteId}` : '/c/machines'}>{r.equipmentCode}</RefLink> : '—' },
                  { label: 'Shared use', value: r.sharedUse ? 'Yes' : 'No' },
                  { label: 'Site', value: r.locationName ?? r.locationCode },
                  { label: 'Season', value: r.seasonCode, mono: true },
                  {
                    label: 'Origin',
                    value: r.originUnitId ? <RefLink to={`/c/batches/${r.originUnitId}`}>{r.originUnitCode ?? 'origin'}</RefLink> : '—',
                  },
                  { label: 'Farmer code', value: r.originFarmerCode, mono: true },
                  { label: 'Operator', value: r.operator?.name },
                  { label: 'Cleanout by', value: r.cleanoutBy?.name },
                  { label: 'Opened', value: <When at={r.openedAt} /> },
                  { label: 'Started', value: <When at={r.startedAt} /> },
                  { label: 'Ended', value: <When at={r.endedAt} /> },
                  {
                    label: 'Previous run',
                    value: r.previousRunId ? <RefLink to={`/c/runs/${r.previousRunId}`}>{r.previousRunCode ?? 'run'}</RefLink> : '—',
                  },
                  { label: 'Flush needed', value: r.flushRequired ? 'Yes' : 'No' },
                  { label: 'Allergen clean', value: r.allergenCleanRequired ? 'Needed' : 'Not needed' },
                ]}
              />
            </Section>
            <Section title="Cleanout photos" eyebrow={r.cleanoutConfirmedAt ? <When at={r.cleanoutConfirmedAt} /> : 'Not confirmed'}>
              {r.cleanoutPhotos.length === 0 && !r.allergenPhoto ? (
                <p className="text-[14px] text-muted">No cleanout photos.</p>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {r.cleanoutPhotos.map((p) => (
                    <figure key={p.id} className="flex flex-col gap-1">
                      <PhotoViewer photo={p.id} alt={`${p.label}, run ${r.code}`} size="md" />
                      <figcaption className="text-[12px] text-muted">{p.label.replace('Cleanout · ', '')}</figcaption>
                    </figure>
                  ))}
                  {(r.allergenPhoto || r.allergenCleanRequired) && (
                    <figure className="flex flex-col gap-1">
                      <PhotoViewer photo={r.allergenPhoto} alt={`Allergen clean, run ${r.code}`} size="md" />
                      <figcaption className="text-[12px] text-muted">Allergen clean</figcaption>
                    </figure>
                  )}
                </div>
              )}
            </Section>
          </>
        }
      />
    </>
  )
}

function pctOf(part: number, whole: number): string {
  if (whole <= 0) return '—'
  return `${((part / whole) * 100).toFixed(2)}%`
}

function BalanceSection({ r }: { r: RunDetail }) {
  const b: RunBalance = r.balance
  const v = balanceVerdict(b)
  const band = useBand(b.band?.id)
  const leftBad = v.leftOver === 'OVER' || v.leftOver === 'SURPLUS' || v.leftOver === 'EXACT_ZERO'
  const line = (label: ReactNode, grams: number, strong = false) => (
    <tr className="border-t border-rule/50">
      <th scope="row" className={strong ? 'py-1.5 pr-3 text-left font-semibold' : 'py-1.5 pr-3 text-left font-normal'}>{label}</th>
      <td className="py-1.5 text-right"><Kg grams={grams} className={strong ? 'font-semibold' : undefined} /></td>
      <td className="py-1.5 pl-3 text-right font-mono text-[12.5px] text-muted tabular">{pctOf(grams, b.inputG)}</td>
    </tr>
  )
  return (
    <Section
      title="Mass balance"
      eyebrow={b.result ? undefined : 'Checked when the run closes'}
      actions={
        b.result ? (
          <span className="flex flex-wrap items-center gap-1">
            <Status status={b.result} label={b.result === 'BALANCED' ? 'Balanced' : 'Out of range'} />
            <BandBadge balance={b} />
          </span>
        ) : undefined
      }
    >
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <table className="w-full text-[14px]">
          <caption className="sr-only">Kilos in and out</caption>
          <tbody>
            {line('Kilos in', b.inputG, true)}
            {line('Flush', b.flushG)}
            {line('Product', b.productG)}
            {line('By-product', b.byproductG)}
            {line('Waste and rejects', b.rejectG)}
            {line('Kilos out', b.outputG, true)}
            <tr className="border-t border-rule">
              <th scope="row" className="py-1.5 pr-3 text-left font-semibold">Left over</th>
              <td className={leftBad ? 'py-1.5 text-right font-semibold text-danger' : 'py-1.5 text-right'}>
                <span className="font-mono tabular">{b.residualG < 0 ? '−' : ''}</span>
                <Kg grams={Math.abs(b.residualG)} className="text-[inherit]" />
              </td>
              <td className={leftBad ? 'py-1.5 pl-3 text-right font-mono text-[12.5px] text-danger tabular' : 'py-1.5 pl-3 text-right font-mono text-[12.5px] text-muted tabular'}>
                {bpText(b.residualBp)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="flex flex-col gap-3">
          <div>
            <div className="mb-1 flex items-baseline justify-between">
              <span className="font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">Product yield</span>
              <span className="font-mono text-[15px] tabular">{bpText(b.yieldBp)}</span>
            </div>
            <YieldBar yieldBp={b.yieldBp} band={b.band} />
            <p className="mt-1 text-[12px] text-muted">Product ÷ (kilos in − flush).</p>
          </div>
          <Facts
            columns={2}
            items={[
              {
                label: 'Left over limit',
                value: (
                  <span className={leftBad ? 'text-danger' : undefined}>
                    {b.lossLimitBp !== null ? bpText(b.lossLimitBp) : '—'}
                    {v.leftOver === 'WITHIN' && ' · within'}
                    {v.leftOver === 'OVER' && ' · over'}
                    {v.leftOver === 'SURPLUS' && ' · more out than in'}
                    {v.leftOver === 'EXACT_ZERO' && ' · exactly zero'}
                  </span>
                ),
              },
              {
                label: 'Range used',
                value: b.band ? (
                  <span>
                    {bandText(b.band)}
                    {b.band.version !== null && <span className="text-muted"> · version {b.band.version}</span>}
                  </span>
                ) : (
                  <span className="text-warn">No range set</span>
                ),
              },
              { label: 'Range approved by', value: band.data ? <UserName id={band.data.approvedById} /> : '—' },
              { label: 'Range from', value: band.data?.effectiveFrom ?? '—', mono: true },
            ]}
          />
          {b.kinds.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {b.kinds.map((k) => (
                <Badge key={k} tone="danger">{BALANCE_KIND_LABEL[k] ?? k}</Badge>
              ))}
            </div>
          )}
        </div>
      </div>
    </Section>
  )
}

function ProblemItem({ run, p }: { run: RunDetail; p: RunProblem }) {
  const open = p.status === 'OPEN'
  const isBalance = p.type === 'BALANCE_OUT_OF_BAND'
  return (
    <li className="flex flex-col gap-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-ink">{exceptionLabel(p.type).en}</div>
          <div className="text-[12.5px] text-muted">
            {problemKindText(p.detail) ?? ''}
            {p.raisedAt && (
              <>
                {problemKindText(p.detail) ? ' · ' : ''}
                <When at={p.raisedAt} />
              </>
            )}
          </div>
        </div>
        <span className="flex shrink-0 flex-wrap items-center gap-1">
          <Severity severity={p.severity} />
          <Status status={p.status} />
          <RefLink to={`/c/exceptions?open=${p.id}`} className="font-sans text-[12.5px]">In the queue</RefLink>
        </span>
      </div>
      {open && isBalance && <RunInvestigation run={run} problem={p} />}
      {open && !isBalance && <ExceptionActions exception={{ ...p, raisedById: null }} />}
      {!open && p.resolutionCode && (
        <div className="text-[12.5px] text-muted">
          Closed with {p.resolutionCode.replace(/_/g, ' ').toLowerCase()}
          {p.resolvedAt && <> · <When at={p.resolvedAt} /></>}
          {p.resolvedById && <> · <UserName id={p.resolvedById} /></>}
        </div>
      )}
    </li>
  )
}

function InputsSection({ r }: { r: RunDetail }) {
  const { scannedIn, issuedNotIn } = r.inputs
  return (
    <Section title="Bags in" eyebrow={issuedNotIn.length ? `${issuedNotIn.length} issued, not scanned in` : undefined} bodyClassName="p-0">
      {scannedIn.length === 0 && issuedNotIn.length === 0 ? (
        <p className="px-4 py-3 text-[14px] text-muted">No bags yet.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <caption className="sr-only">Bags scanned in and issued</caption>
            <thead>
              <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                <th scope="col" className="px-4 py-1.5">Tag</th>
                <th scope="col" className="px-2 py-1.5">Lot</th>
                <th scope="col" className="px-2 py-1.5 text-right">Kg</th>
                <th scope="col" className="hidden px-2 py-1.5 md:table-cell">When</th>
                <th scope="col" className="px-4 py-1.5">State</th>
              </tr>
            </thead>
            <tbody>
              {scannedIn.map((i) => (
                <tr key={i.id} className="border-t border-rule/50">
                  <td className="px-4 py-1.5"><Code>{i.tag ?? '—'}</Code></td>
                  <td className="px-2 py-1.5"><RefLink to={`/c/batches/${i.unitId}`}>{i.unitCode}</RefLink></td>
                  <td className="px-2 py-1.5 text-right"><Kg grams={i.weightG} /></td>
                  <td className="hidden px-2 py-1.5 md:table-cell"><When at={i.scannedAt} /></td>
                  <td className="px-4 py-1.5"><Status status="OK" label="Scanned in" /></td>
                </tr>
              ))}
              {issuedNotIn.map((i) => (
                <tr key={i.containerId} className="border-t border-rule/50 bg-warn-wash/40">
                  <td className="px-4 py-1.5"><Code>{i.tag ?? '—'}</Code></td>
                  <td className="px-2 py-1.5"><RefLink to={`/c/batches/${i.unitId}`}>{i.unitCode}</RefLink></td>
                  <td className="px-2 py-1.5 text-right"><Kg grams={i.issueNetG} /></td>
                  <td className="hidden px-2 py-1.5 md:table-cell"><When at={i.issuedAt} /></td>
                  <td className="px-4 py-1.5">
                    <span className="flex flex-wrap items-center gap-1">
                      <Status status="PENDING" label="Issued, not scanned in" />
                      {i.shipmentId && <RefLink to={`/c/shipments/${i.shipmentId}`}>{i.shipmentCode ?? 'issue'}</RefLink>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Section>
  )
}

function OutputBlock({ r, o }: { r: RunDetail; o: RunOutput }) {
  const label = o.isFlush ? 'Flush' : (r.step.outputs.find((x) => x.key === o.streamKey)?.label ?? unitTypeLabel(o.unitType))
  return (
    <div className="border-b border-rule/70 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2 bg-paper-tint/50 px-4 py-2">
        <RefLink to={`/c/batches/${o.id}`}>{o.code}</RefLink>
        <span className="text-[13px] text-ink-soft">{label}</span>
        <Stream stream={o.stream} />
        {o.hold !== 'NONE' && <Status status={o.hold} label={o.holdReasons.includes('RUN_EXCEPTION') ? 'On hold · run problem' : undefined} />}
        <span className="ml-auto"><Kg grams={o.netG} /></span>
      </div>
      {o.sacks.length === 0 ? (
        <p className="px-4 py-2 text-[13px] text-muted">No sacks.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-[14px]">
            <caption className="sr-only">Sacks of {o.code}</caption>
            <thead>
              <tr className="text-left font-mono text-[10.5px] font-bold uppercase tracking-label text-muted">
                <th scope="col" className="px-4 py-1.5">Tag</th>
                <th scope="col" className="px-2 py-1.5 text-right">Kg</th>
                <th scope="col" className="hidden px-2 py-1.5 sm:table-cell">Stream</th>
                <th scope="col" className="px-2 py-1.5">Place</th>
                <th scope="col" className="px-4 py-1.5">Status</th>
              </tr>
            </thead>
            <tbody>
              {o.sacks.map((s) => (
                <tr key={s.containerId} className="border-t border-rule/50">
                  <td className="px-4 py-1.5">
                    <Code>{s.tag ?? s.conflictTag ?? '—'}</Code>
                    {!s.tag && s.conflictTag && <Badge tone="danger" className="ml-2">Tag clash</Badge>}
                  </td>
                  <td className="px-2 py-1.5 text-right"><Kg grams={s.netG} /></td>
                  <td className="hidden px-2 py-1.5 sm:table-cell"><Stream stream={s.stream} /></td>
                  <td className="px-2 py-1.5 text-[13px]">{s.placePath ?? <span className="text-muted">—</span>}</td>
                  <td className="px-4 py-1.5"><Status status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
